'use client'

import React, { useCallback } from 'react'
import { useLocale, useTranslations } from 'next-intl'

import type { HolidayPackageSummary } from '@/src/types/holidays'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import useHolidayAppMode from '@/src/hooks/holidays/useHolidayAppMode'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'
import { buildPackageHref, formatHolidayPrice, formatHolidayTripWindow, withHolidayAppMode } from '@/src/utils/holidays'

import PackageCard from './packageCard'

interface PackageCardWrapperProps {
    summary: HolidayPackageSummary
    collectionSlug: string
    /** Section the card was listed in, and its 1-based place in that list. */
    sourceSection: string
    cardPosition: number
    isMobile: boolean
}

const PackageCardWrapper: React.FC<PackageCardWrapperProps> = ({
    summary,
    collectionSlug,
    sourceSection,
    cardPosition,
    isMobile
}) => {
    const t = useTranslations('holidaysPage')
    const locale = useLocale()
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()
    const isApp = useHolidayAppMode()

    // The card the reader picked, not the page they picked it from, so the package keys are
    // restated here — on a related-packages grid the page context describes a different package.
    const handleSelect = useCallback(() => {
        triggerHolidayUIActionEvent(HOLIDAY_ACTION.PACKAGE_SELECTED, {
            action_type: HOLIDAY_ACTION_TYPE.NAVIGATION,
            package_id: summary.slug,
            package_name: summary.destinationName,
            destination: summary.destinationName,
            source_section: sourceSection,
            card_position: cardPosition,
            campaign_theme: collectionSlug,
            adult_price: summary.startingPrice.amount,
            price_currency: summary.startingPrice.currency
        })
    }, [triggerHolidayUIActionEvent, summary, sourceSection, cardPosition, collectionSlug])

    const durationLabel = t('durationDays', { count: summary.durationDays })
    const tripWindowLabel = t('groupTripOn', {
        window: formatHolidayTripWindow(summary.startDate, summary.endDate, locale)
    })

    return (
        <PackageCard
            href={withHolidayAppMode(buildPackageHref(collectionSlug, summary.slug), isApp)}
            image={summary.thumbnail}
            badge={summary.badge}
            airlineName={summary.airline?.name}
            destinationName={summary.destinationName}
            durationLabel={durationLabel}
            tourPackageLabel={t('tourPackage')}
            tagline={summary.tagline}
            tags={summary.tags ?? []}
            tripWindowLabel={tripWindowLabel}
            startingFromLabel={t('startingFrom')}
            priceLabel={formatHolidayPrice(summary.startingPrice, locale)}
            viewDetailsLabel={t('viewDetails')}
            onSelect={handleSelect}
            isMobile={isMobile}
        />
    )
}

export { PackageCardWrapper }
export default PackageCardWrapper
